import { clamp01, easeOutBack, prefersReducedMotion } from './motion.js';
import type { Camera } from './Camera.js';
import type { ScreenPoint } from '../types.js';

interface Pop {
    kind: 'pop';
    x: number;
    z: number;
    started: number;
}

interface Burst {
    kind: 'burst';
    x: number;
    z: number;
    started: number;
    colour: string;
    seeds: number[];
}

type Effect = Pop | Burst;

const POP_MS = 340;
const BURST_MS = 620;
const BURST_PARTICLES = 7;

export class Effects {
    private items: Effect[] = [];

    constructor(private readonly camera: Camera) {}

    get busy(): boolean {
        return this.items.length > 0;
    }

    pop(x: number, z: number): void {
        if (prefersReducedMotion()) return;
        this.items.push({ kind: 'pop', x, z, started: performance.now() });
    }

    burst(x: number, z: number, colour: string): void {
        if (prefersReducedMotion()) return;

        const seeds = Array.from({ length: BURST_PARTICLES }, () => Math.random());
        this.items.push({ kind: 'burst', x, z, started: performance.now(), colour, seeds });
    }

    popScale(x: number, z: number, now = performance.now()): number {
        const item = this.items.find(
            (e): e is Pop => e.kind === 'pop' && e.x === x && e.z === z,
        );
        if (!item) return 1;

        const t = clamp01((now - item.started) / POP_MS);
        return 0.72 + easeOutBack(t) * 0.28;
    }

    prune(now = performance.now()): void {
        this.items = this.items.filter((item) => {
            const life = item.kind === 'pop' ? POP_MS : BURST_MS;
            return now - item.started < life;
        });
    }

    draw(ctx: CanvasRenderingContext2D, now = performance.now()): void {
        this.items.forEach((item) => {
            if (item.kind === 'burst') this.drawBurst(ctx, item, now);
        });
    }

    private drawBurst(ctx: CanvasRenderingContext2D, burst: Burst, now: number): void {
        const t = clamp01((now - burst.started) / BURST_MS);
        const origin: ScreenPoint = this.camera.worldToCanvas(burst.x, burst.z);

        ctx.save();
        ctx.fillStyle = burst.colour;

        burst.seeds.forEach((seed, i) => {
            const angle = (i / BURST_PARTICLES) * Math.PI * 2 + seed;
            const spread = 26 + seed * 22;

            const x = origin.x + Math.cos(angle) * spread * t;
            const y = origin.y - 34 - t * 46 + Math.sin(angle) * spread * t * 0.4;

            ctx.globalAlpha = (1 - t) * 0.9;
            ctx.beginPath();
            ctx.arc(x, y, 4 - t * 2, 0, Math.PI * 2);
            ctx.fill();
        });

        ctx.restore();
    }
}
